/*
 * The admin's tools (the operator's Discord account on live; everyone on a
 * test server started with FERRO_ADMINS=any). The hello carries `admin`
 * only for an admin; requests go as {type: "admin", op} and come back as
 * {type: "admin", ok, text, ...}, in order.
 */

import { createSignal } from "solid-js";

export interface AdminFlags {
  /** This connection sees everything, fog or not. */
  god: boolean;
  /** Fog of war is on for everyone. */
  fog: boolean;
}

export interface AdminStatus {
  tick: number;
  version: string;
  fog: boolean;
  resident_mb: number;
  /** Per side. */
  units: number[];
  seats: number[];
  players: { side: number; name: string; avatar: string }[];
  homes: ([number, number] | null)[];
  /** Design names by id. */
  designs: string[];
}

const [admin, setAdmin] = createSignal<AdminFlags | null>(null);
const [status, setStatus] = createSignal<AdminStatus | null>(null);
const [log, setLog] = createSignal<{ ok: boolean; text: string; at: number }[]>([]);
export { admin, status, log };

/** A seat made for an AI player: its name and session token, shown once
 *  and then forgotten (in memory only: never stored, never logged). */
const [botSeat, setBotSeat] = createSignal<{ name: string; token: string } | null>(null);
export { botSeat };
export const forgetBotSeat = () => setBotSeat(null);
let askedBot = "";

let send: (c: Record<string, unknown>) => void = () => {};
/** Waiting for replies, oldest first (replies come in order): whether
 *  each was a status request (kept out of the log). */
const waiting: boolean[] = [];

/** From each hello: admin or not (absent: not). */
export function setAdminHello(m: Record<string, unknown>): void {
  const a = m.admin as AdminFlags | undefined;
  setAdmin(a ? { god: !!a.god, fog: !!a.fog } : null);
}

export function setAdminSend(f: (c: Record<string, unknown>) => void): void {
  send = f;
}

/** A reply from the server. */
export function adminReply(m: Record<string, unknown>): void {
  const quiet = waiting.shift() ?? false;
  if (m.status) setStatus(m.status as AdminStatus);
  // A bot's seat: the token to show once (and never in the log).
  const token = typeof m.token === "string" ? m.token : "";
  if (token) setBotSeat({ name: askedBot, token });
  if (quiet && m.ok !== false) return;
  const text = String(m.text ?? (m.ok === false ? "Refused" : "Done"));
  setLog((l) => [{ ok: m.ok !== false, text: token ? text.split(token).join("(token)") : text, at: Date.now() }, ...l].slice(0, 12));
}

/** An operator request (`op` as the server takes it). */
export function adminOp(op: Record<string, unknown>): void {
  if (typeof op.bot_seat === "string") askedBot = op.bot_seat;
  waiting.push(!!op.status);
  send({ type: "admin", op });
}

/** See everything on this connection (or stop). */
export function godView(on: boolean): void {
  send({ type: "god", on });
}

/** The camera to a hex (the game sets it). */
let look: (at: [number, number]) => void = () => {};
export function setAdminLook(f: (at: [number, number]) => void): void {
  look = f;
}
export const lookAt = (at: [number, number]) => look(at);

/** Picking a hex on the map for a tool (the game sets it): the next click
 *  on the map calls back with it (null: stop picking). */
let picker: (cb: ((at: [number, number]) => void) | null) => void = () => {};
export function setAdminPicker(f: typeof picker): void {
  picker = f;
}
export const pickHex = (cb: ((at: [number, number]) => void) | null) => picker(cb);
